import type { ReactNode } from 'react';
import type { DdPhaseSummary } from '../api/types';
import { Icon } from './icons';

/**
 * The checklist toolbar (redesign slice 3): find an item by its title, detail or
 * category, and narrow the list to one phase. Both live in the page address
 * (`?q=&phase=`) with the filter tile, and both work with it ("missing" + "bank").
 * The "Showing …" line sits under them.
 */
export function ChecklistToolbar({
  query,
  onQueryChange,
  phaseId,
  phases,
  onPhaseChange,
  showing,
  actions,
}: {
  query: string;
  onQueryChange: (query: string) => void;
  phaseId: string | null;
  /** Every phase, in order (the phaseless legacy bucket included). */
  phases: DdPhaseSummary[];
  onPhaseChange: (phaseId: string | null) => void;
  /** The "Showing … · Show all" line. */
  showing: ReactNode;
  /** Buttons at the end of the row (Add item). */
  actions?: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
        <label className="flex min-w-0 flex-1 basis-64 flex-col gap-1 text-xs font-medium text-ink-2">
          Find an item
          <span className="relative flex items-center">
            <Icon name="search" size={14} className="pointer-events-none absolute left-2.5 text-ink-3" />
            <input
              type="search"
              value={query}
              placeholder="Title, detail or category"
              onChange={(e) => onQueryChange(e.target.value)}
              className="h-9 w-full rounded-md border border-input bg-background pl-8 pr-2 text-sm text-ink max-md:min-h-tap"
            />
          </span>
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-ink-2 max-md:w-full">
          Phase
          <select
            value={phaseId ?? ''}
            onChange={(e) => onPhaseChange(e.target.value || null)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm text-ink max-md:min-h-tap"
          >
            <option value="">All phases</option>
            {phases.map((p) => (
              <option key={p.phaseId ?? 'legacy'} value={p.phaseId ?? 'legacy'}>
                {p.name ?? 'Items not in a phase'}
              </option>
            ))}
          </select>
        </label>
        {actions ? <div className="flex items-end gap-2 max-md:w-full">{actions}</div> : null}
      </div>
      {showing}
    </div>
  );
}
